import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { EmptyStateCard, GlassCard, Pill, ProgressBar } from '../components/ui';
import { AchievementBadge, PremiumCard, PremiumEmpty } from '../components/premium';
import { NavIcon } from '../components/NavIcons';
import { api, apiForm, ApiError } from '../lib/api';
import { kgToLbs } from '../lib/units';
import type { BodyCheckInRow, GoalRow, PersonalRecordRow } from '../types';
import { btnDestructive, btnPrimary, btnSecondary, fieldControl, panelInset } from '../lib/uiClasses';

type GoalFormType = 'lift' | 'weight' | 'body_fat' | 'lean_mass' | 'custom';

type WeightUnit = 'kg' | 'lb';

function formatWeightKg(kg: number, imperial: boolean): string {
  if (imperial) return `${Math.round(kgToLbs(kg) * 10) / 10} lb`;
  return `${Math.round(kg * 10) / 10} kg`;
}

function formatCheckInWeightKg(kg: number | null, imperial: boolean): string {
  if (kg == null) return '—';
  return formatWeightKg(kg, imperial);
}

function TrendSparkline({ values, stroke }: { values: number[]; stroke: string }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = span * 0.06;
  const lo = min - pad;
  const hi = max + pad;
  const range = hi - lo || 1;
  const w = 128;
  const h = 40;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - lo) / range) * (h - 2) - 1;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-10 w-full max-w-[9rem]" preserveAspectRatio="none" aria-hidden>
      <polyline fill="none" strokeWidth="1.5" points={pts} stroke={stroke} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function goalTypeLabel(type: string): string {
  switch (type) {
    case 'lift':
      return 'Strength';
    case 'weight':
      return 'Weight';
    case 'body_fat':
      return 'Body fat';
    case 'lean_mass':
      return 'Lean mass';
    case 'custom':
      return 'Custom';
    default:
      return type;
  }
}

function metadataNotes(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object' || metadata === null) return null;
  const n = (metadata as { notes?: unknown }).notes;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}

function tipsForGoalType(t: GoalFormType | string): string[] {
  switch (t) {
    case 'lift':
      return [
        'Use the exact exercise name you log in workouts so PRs sync automatically.',
        'Progress uses an estimated 1-rep max from your best set—log challenging sets with solid form.',
        'When you hit the top of your rep range, add a small amount of weight (about 2.5–5 lb / 1–2.5 kg on many upper lifts).',
        'Sleep, protein, and consistency beat switching programs every week when strength stalls.',
      ];
    case 'weight':
      return [
        'Compare your weekly average scale weight, not single-day jumps, especially when cutting or bulking.',
        'Small, sustainable calorie changes beat harsh restriction; align with your profile targets when you can.',
        'Keep lifting through a cut to preserve muscle; emphasize protein on a bulk to limit excess fat gain.',
      ];
    case 'body_fat':
      return [
        'Visual and tape measurements often track recomposition better than day-to-day scale changes.',
        'Strength work plus adequate protein moves body composition more reliably than cardio alone.',
        'Water retention fluctuates; judge trends over several weeks, not one weigh-in.',
      ];
    case 'lean_mass':
      return [
        'Lean mass here is estimated from your latest scale weight and body-fat % on check-ins (or a profile body-fat estimate). Log both when you can.',
        'Recomposition often means the scale is flat while strength and measurements improve—track multiple signals.',
        'Protein near 1.6–2.2 g/kg body weight and progressive overload are the main levers for more lean tissue.',
      ];
    case 'custom':
    default:
      return [
        'Split the outcome into smaller milestones with dates so you can correct course early.',
        'Treat key sessions as non-negotiable appointments—you can miss a day, not the habit.',
        'For running and endurance, easy weekly volume plus one harder session usually beats all-out work daily.',
      ];
  }
}

function inputClass() {
  return fieldControl;
}

/** iOS: 16px+ on native pickers avoids zoom; still use text-sm from `sm` up */
function dateInputClass() {
  return `${inputClass()} text-base sm:text-sm`;
}

function labelClass() {
  return 'block text-xs font-medium uppercase tracking-wide text-zinc-500';
}

export function ProgressPage() {
  const { user } = useAuth();
  const imperial = user?.preferredUnitSystem === 'imperial';

  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [records, setRecords] = useState<PersonalRecordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadWarning, setLoadWarning] = useState<string | null>(null);

  const [checkIns, setCheckIns] = useState<BodyCheckInRow[]>([]);
  const [ciDate, setCiDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [ciWeight, setCiWeight] = useState('');
  const [ciBf, setCiBf] = useState('');
  const [ciNotes, setCiNotes] = useState('');
  const [ciSaving, setCiSaving] = useState(false);
  const [goalType, setGoalType] = useState<GoalFormType>('lift');
  const [title, setTitle] = useState('');
  const [exerciseName, setExerciseName] = useState('');
  const [target, setTarget] = useState('');
  const [liftUnit, setLiftUnit] = useState(imperial ? 'lb' : 'kg');
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(imperial ? 'lb' : 'kg');
  const [customUnit, setCustomUnit] = useState('min');
  const [currentOptional, setCurrentOptional] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setLiftUnit(imperial ? 'lb' : 'kg');
    setWeightUnit(imperial ? 'lb' : 'kg');
  }, [imperial]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadWarning(null);
    try {
      await api('/api/progress/sync', { method: 'POST' });
      const [g, pr] = await Promise.all([
        api<GoalRow[]>('/api/goals'),
        api<PersonalRecordRow[]>('/api/progress/personal-records'),
      ]);
      setGoals(g);
      setRecords(pr);
      try {
        setCheckIns(await api<BodyCheckInRow[]>('/api/body-check-ins'));
      } catch {
        setCheckIns([]);
        setLoadWarning(
          'Body check-ins could not load. From the project database folder run npx prisma db push, then restart the API server.',
        );
      }
    } catch {
      setError('Could not load progress data. Try again.');
      setGoals([]);
      setRecords([]);
      setCheckIns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  function onGoalTypeChange(v: GoalFormType) {
    setGoalType(v);
    if (v === 'lift') setLiftUnit(imperial ? 'lb' : 'kg');
    else if (v === 'weight' || v === 'lean_mass') setWeightUnit(imperial ? 'lb' : 'kg');
    else if (v === 'custom') setCustomUnit('min');
  }

  function buildMetadata(): Record<string, unknown> | undefined {
    const notesTrim = notes.trim();
    if (goalType === 'lift') {
      const m: Record<string, unknown> = { exerciseKey: exerciseName.trim() };
      if (notesTrim) m.notes = notesTrim;
      return m;
    }
    if (notesTrim) return { notes: notesTrim };
    return undefined;
  }

  async function addGoal() {
    if (!title.trim() || !target.trim()) return;
    const targetNum = Number(target);
    if (!Number.isFinite(targetNum) || targetNum <= 0) return;

    if (goalType === 'lift' && !exerciseName.trim()) return;

    const unitForGoal =
      goalType === 'weight' || goalType === 'lean_mass'
        ? weightUnit
        : goalType === 'body_fat'
          ? '%'
          : goalType === 'lift'
            ? liftUnit
            : customUnit.trim() || 'units';

    let body: Record<string, unknown> = {
      type: goalType,
      title: title.trim(),
      targetValue: targetNum,
      unit: unitForGoal,
      ...(targetDate.trim() ? { targetDate: targetDate.trim() } : {}),
    };

    const meta = buildMetadata();
    if (meta) body.metadata = meta;

    const curRaw = currentOptional.trim();
    if (curRaw && goalType !== 'lift') {
      const c = Number(curRaw);
      if (Number.isFinite(c)) body.currentValue = c;
    }

    setSaving(true);
    setError(null);
    try {
      await api('/api/goals', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setTitle('');
      setExerciseName('');
      setTarget('');
      setCurrentOptional('');
      setTargetDate('');
      setNotes('');
      await loadData();
    } catch {
      setError('Could not save goal.');
    } finally {
      setSaving(false);
    }
  }

  async function removeGoal(id: string) {
    if (!window.confirm('Remove this goal?')) return;
    try {
      await api(`/api/goals/${id}`, { method: 'DELETE' });
      await loadData();
    } catch {
      setError('Could not delete goal.');
    }
  }

  async function removeCheckIn(id: string) {
    if (!window.confirm('Delete this check-in?')) return;
    try {
      await api(`/api/body-check-ins/${id}`, { method: 'DELETE' });
      await loadData();
    } catch {
      setError('Could not delete check-in.');
    }
  }

  async function saveCheckIn() {
    const body: Record<string, unknown> = {
      weightUnit: imperial ? 'lb' : 'kg',
      measuredAt: new Date(`${ciDate}T12:00:00`).toISOString(),
    };
    if (ciWeight.trim()) {
      const w = Number(ciWeight);
      if (!Number.isFinite(w) || w <= 0) {
        setError('Enter a valid weight or leave it blank.');
        return;
      }
      body.weight = w;
    }
    if (ciBf.trim()) {
      const bf = Number(ciBf);
      if (!Number.isFinite(bf) || bf < 0 || bf > 70) {
        setError('Body fat should be between 0 and 70%, or leave blank.');
        return;
      }
      body.bodyFatPct = bf;
    }
    if (ciNotes.trim()) body.notes = ciNotes.trim();
    if (body.weight == null && body.bodyFatPct == null && body.notes == null) {
      setError('Add weight, body fat %, and/or a note.');
      return;
    }
    setCiSaving(true);
    setError(null);
    try {
      await api('/api/body-check-ins', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setCiWeight('');
      setCiBf('');
      setCiNotes('');
      setCiDate(new Date().toISOString().slice(0, 10));
      await loadData();
    } catch {
      setError('Could not save check-in.');
    } finally {
      setCiSaving(false);
    }
  }

  const formTips = tipsForGoalType(goalType);

  const checkInsAsc = useMemo(
    () =>
      [...checkIns].sort(
        (a, b) => new Date(a.measuredAt).getTime() - new Date(b.measuredAt).getTime(),
      ),
    [checkIns],
  );
  const weightTrend = useMemo(
    () =>
      checkInsAsc
        .filter((c) => c.weightKg != null)
        .map((c) => (imperial ? kgToLbs(c.weightKg!) : c.weightKg!)),
    [checkInsAsc, imperial],
  );
  const bfTrend = useMemo(
    () => checkInsAsc.filter((c) => c.bodyFatPct != null).map((c) => c.bodyFatPct!),
    [checkInsAsc],
  );

  return (
    <div className="min-w-0 space-y-4 pb-2">
      <h1 className="font-display text-2xl font-bold tracking-tight text-white">Progress</h1>

      {error ? (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>
      ) : null}
      {loadWarning ? (
        <p className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
          {loadWarning}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void loadData()}
          disabled={loading}
          className={btnSecondary}
        >
          {loading ? 'Refreshing…' : 'Sync from workouts'}
        </button>
      </div>
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <PremiumCard className="min-h-[200px]">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h2 className="font-display text-base font-semibold text-white">Personal records</h2>
            <Pill>From workouts</Pill>
          </div>
          {loading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : records.length === 0 ? (
            <PremiumEmpty
              icon={<NavIcon name="workouts" className="h-6 w-6" />}
              title="No PRs yet"
              action={
                <Link to="/workouts" className={btnPrimary}>
                  Go to Train
                </Link>
              }
            />
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {records.map((r) => (
                <li key={r.id}>
                  <AchievementBadge
                    label={r.displayName}
                    value={`${formatWeightKg(r.estOneRmKg, imperial)} est. 1RM`}
                    tone="gold"
                  />
                  <div className="mt-1 px-1 text-[10px] text-zinc-600">
                    {r.bestReps}×{formatWeightKg(r.bestWeightKg, imperial)} ·{' '}
                    {new Date(r.achievedAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PremiumCard>

        <GlassCard>
          <div className="mb-4 flex items-center justify-between gap-2">
            <h2 className="text-lg font-medium text-white">Your goals</h2>
            <Pill>{goals.length} active</Pill>
          </div>
          {loading ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : goals.length === 0 ? (
            <EmptyStateCard
              title="Add a goal to track progress automatically"
              description="Strength goals sync from your workout log (estimated 1RM). Weight and body-fat goals can pull from check-ins."
            >
              <a href="#plan-new-goal" className={btnPrimary}>
                Plan a new goal
              </a>
            </EmptyStateCard>
          ) : (
            <ul className="max-h-[540px] space-y-4 overflow-y-auto pr-1 nice-scroll">
              {goals.map((g) => {
                const displayCur = g.resolvedCurrentValue ?? g.currentValue;
                const showLiftBar = g.type === 'lift' && displayCur != null && g.targetValue > 0;
                const showLeanBar = g.type === 'lean_mass' && displayCur != null && g.targetValue > 0;
                const pct = showLiftBar ? Math.min(100, Math.max(0, (displayCur! / g.targetValue) * 100)) : null;
                const pctLean = showLeanBar ? Math.min(100, Math.max(0, (displayCur! / g.targetValue) * 100)) : null;
                const gNotes = metadataNotes(g.metadata);
                const tips = tipsForGoalType(g.type as GoalFormType);

                return (
                  <li key={g.id} className={`${panelInset} px-5 py-4`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-zinc-50">{g.title}</span>
                          <Pill>{goalTypeLabel(g.type)}</Pill>
                          {g.targetDate ? (
                            <span className="text-xs text-zinc-500">
                              Target:{' '}
                              {new Date(g.targetDate).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          ) : null}
                        </div>
                        {g.type === 'lift' && g.linkedExerciseKey ? (
                          <p className="mt-1.5 text-xs font-medium text-sky-400 sm:text-sm">
                            Exercise: {g.linkedExerciseKey}
                          </p>
                        ) : null}
                        {gNotes ? (
                          <p className="mt-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-xs leading-relaxed text-zinc-300">
                            {gNotes}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => void removeGoal(g.id)}
                        className={`${btnDestructive} shrink-0`}
                        aria-label={`Remove goal ${g.title}`}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mt-4 text-sm text-zinc-400">
                      {displayCur != null ? (
                        <>
                          Current
                          {g.type === 'lift' ? (
                            <span className="text-zinc-500"> (est. 1RM from logged sets)</span>
                          ) : null}
                          :{' '}
                          <span className="font-medium tabular-nums text-zinc-100">
                            {Math.round(displayCur * 10) / 10}
                          </span>
                          {' → '}
                          target{' '}
                          <span className="font-medium tabular-nums text-zinc-100">
                            {Math.round(g.targetValue * 10) / 10} {g.unit}
                          </span>
                        </>
                      ) : (
                        <>
                          Target:{' '}
                          <span className="text-zinc-200">
                            {Math.round(g.targetValue * 10) / 10} {g.unit}
                          </span>
                          {g.type === 'lift' ? (
                            <span className="ml-2 text-zinc-500">(fills in when you log this lift)</span>
                          ) : g.type === 'lean_mass' ? (
                            <span className="ml-2 text-zinc-500">(fills in from check-ins with weight + body fat %)</span>
                          ) : null}
                        </>
                      )}
                    </div>

                    {g.resolvedViaCheckIn && g.resolvedCheckInAt ? (
                      <p className="mt-2 text-xs text-emerald-300/85">
                        Current reflects your latest check-in (
                        {new Date(g.resolvedCheckInAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        ).
                      </p>
                    ) : null}

                    {showLiftBar && pct != null ? (
                      <div className="mt-4">
                        <div className="mb-1.5 flex items-baseline justify-between gap-2">
                          <span className="text-xs font-medium text-zinc-500">
                            Progress toward target (est. 1RM)
                          </span>
                          <span className="text-lg font-semibold tabular-nums text-white sm:text-xl">
                            {Math.round(pct)}%
                          </span>
                        </div>
                        <ProgressBar
                          value={displayCur!}
                          max={g.targetValue}
                          colorClass="bg-gradient-to-r from-sky-600 to-cyan-400"
                        />
                      </div>
                    ) : null}

                    {showLeanBar && pctLean != null ? (
                      <div className="mt-4">
                        <div className="mb-1.5 flex items-baseline justify-between gap-2">
                          <span className="text-xs font-medium text-zinc-500">
                            Progress toward lean mass target
                          </span>
                          <span className="text-lg font-semibold tabular-nums text-white sm:text-xl">
                            {Math.round(pctLean)}%
                          </span>
                        </div>
                        <ProgressBar
                          value={displayCur!}
                          max={g.targetValue}
                          colorClass="bg-gradient-to-r from-emerald-600 to-teal-400"
                        />
                      </div>
                    ) : null}

                    {(g.type === 'weight' || g.type === 'lean_mass') && g.timelineWeeks != null ? (
                      <p className="mt-2 text-xs text-zinc-500">
                        Roughly {g.timelineWeeks} week{g.timelineWeeks === 1 ? '' : 's'} to target at the estimated pace (
                        {g.unit}
                        ).
                      </p>
                    ) : null}

                    <div className="mt-4 rounded-xl border border-white/10 bg-black/25 px-3 py-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        Pointers
                      </p>
                      <ul className="mt-2 list-inside list-disc space-y-1.5 text-xs leading-relaxed text-zinc-400">
                        {tips.slice(0, 2).map((line, i) => (
                          <li key={i} className="marker:text-zinc-600">
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </GlassCard>
      </div>

      <GlassCard className="mt-6" delay={0.06}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-white">Body check-ins</h2>
            <p className="mt-1 max-w-xl text-sm text-zinc-500">
              Track weight and body fat over time. Goals use your latest entry unless you set a manual &quot;current&quot;
              on the goal.
            </p>
          </div>
          <div className="flex gap-4 text-xs text-zinc-500">
            <div className="flex flex-col items-end gap-1">
              <span className="font-medium uppercase tracking-wide text-zinc-600">Weight trend</span>
              {weightTrend.length >= 2 ? (
                <TrendSparkline values={weightTrend} stroke="rgb(56, 189, 248)" />
              ) : (
                <span className="text-zinc-600">Need 2+ entries</span>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="font-medium uppercase tracking-wide text-zinc-600">Body fat %</span>
              {bfTrend.length >= 2 ? (
                <TrendSparkline values={bfTrend} stroke="rgb(167, 139, 250)" />
              ) : (
                <span className="text-zinc-600">Need 2+ entries</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 grid min-w-0 gap-4 border-t border-white/5 pt-6 lg:grid-cols-2">
          <div className="min-w-0 space-y-3">
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="ci-date" className={labelClass()}>
                  Date
                </label>
                <input
                  id="ci-date"
                  type="date"
                  value={ciDate}
                  onChange={(e) => setCiDate(e.target.value)}
                  className={`${dateInputClass()} mt-1.5`}
                />
              </div>
              <div>
                <label htmlFor="ci-weight" className={labelClass()}>
                  Weight ({imperial ? 'lb' : 'kg'})
                </label>
                <input
                  id="ci-weight"
                  inputMode="decimal"
                  placeholder="optional"
                  value={ciWeight}
                  onChange={(e) => setCiWeight(e.target.value)}
                  className={`${inputClass()} mt-1.5 tabular-nums`}
                />
              </div>
              <div>
                <label htmlFor="ci-bf" className={labelClass()}>
                  Body fat %
                </label>
                <input
                  id="ci-bf"
                  inputMode="decimal"
                  placeholder="optional"
                  value={ciBf}
                  onChange={(e) => setCiBf(e.target.value)}
                  className={`${inputClass()} mt-1.5 tabular-nums`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="ci-notes" className={labelClass()}>
                Note (optional)
              </label>
              <input
                id="ci-notes"
                placeholder="e.g. morning fasted, post-travel"
                value={ciNotes}
                onChange={(e) => setCiNotes(e.target.value)}
                className={`${inputClass()} mt-1.5`}
              />
            </div>
            <button
              type="button"
              disabled={ciSaving}
              onClick={() => void saveCheckIn()}
              className={btnPrimary}
            >
              {ciSaving ? 'Saving…' : 'Save check-in'}
            </button>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Recent entries</div>
            {loading ? (
              <p className="text-sm text-zinc-500">Loading…</p>
            ) : checkIns.length === 0 ? (
              <EmptyStateCard
                title="Add your first check-in to unlock weight trends"
                description="Log weight and optional body fat here. Goals and charts stay in sync with your latest entry."
              />
            ) : (
              <ul className="max-h-[280px] space-y-2 overflow-y-auto pr-1 nice-scroll text-sm">
                {checkIns.map((row) => (
                  <li
                    key={row.id}
                    className={`flex flex-wrap items-start justify-between gap-2 ${panelInset} px-3 py-2.5`}
                  >
                    <div>
                      <div className="tabular-nums text-zinc-200">
                        {new Date(row.measuredAt).toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="mt-1 text-xs text-zinc-400">
                        <span className="text-zinc-300">Weight:</span> {formatCheckInWeightKg(row.weightKg, imperial)}
                        {' · '}
                        <span className="text-zinc-300">Body fat:</span>{' '}
                        {row.bodyFatPct != null ? `${row.bodyFatPct}%` : '—'}
                      </div>
                      {row.notes ? <p className="mt-1 text-xs text-zinc-500">{row.notes}</p> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => void removeCheckIn(row.id)}
                      className={`${btnDestructive} shrink-0 text-[11px]`}
                      aria-label="Delete check-in"
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </GlassCard>

      <GlassCard id="plan-new-goal" className="mt-6 scroll-mt-24 min-w-0" delay={0.08}>
        <h2 className="text-lg font-medium text-white">Plan a new goal</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Pick a type, add a title, and set a target. Strength goals use the same exercise name as in your workouts.
        </p>

        <div className="mt-6 grid min-w-0 gap-8 lg:grid-cols-3">
          <div className="min-w-0 space-y-4 lg:col-span-2">
            <div>
              <label htmlFor="goal-type" className={labelClass()}>
                Goal category
              </label>
              <select
                id="goal-type"
                value={goalType}
                onChange={(e) => onGoalTypeChange(e.target.value as GoalFormType)}
                className={`${inputClass()} mt-1.5`}
              >
                <option value="lift">Strength / lift (bench, squat, deadlift, etc.)</option>
                <option value="weight">Scale weight</option>
                <option value="body_fat">Body fat percentage</option>
                <option value="lean_mass">Lean body mass (from check-ins)</option>
                <option value="custom">Custom (run pace, mile time, habit, etc.)</option>
              </select>
            </div>

            <div>
              <label htmlFor="goal-title" className={labelClass()}>
                Goal title
              </label>
              <input
                id="goal-title"
                placeholder='e.g. "Hit a 225 lb bench" or "Reach 12% body fat"'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`${inputClass()} mt-1.5`}
              />
            </div>

            {goalType === 'lift' ? (
              <div>
                <label htmlFor="exercise-name" className={labelClass()}>
                  Linked exercise (exact workout name)
                </label>
                <input
                  id="exercise-name"
                  placeholder="e.g. Barbell Bench Press"
                  value={exerciseName}
                  onChange={(e) => setExerciseName(e.target.value)}
                  className={`${inputClass()} mt-1.5 font-mono text-sm`}
                />
                <p className="mt-1.5 text-xs text-zinc-500">
                  Must match spelling in your training log. The app estimates your current strength from logged sets and updates this goal automatically.
                </p>
              </div>
            ) : null}

            <div className="grid min-w-0 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="target-value" className={labelClass()}>
                  Target value
                </label>
                <input
                  id="target-value"
                  inputMode="decimal"
                  placeholder="e.g. 100"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className={`${inputClass()} mt-1.5`}
                />
              </div>
              <div>
                <label htmlFor="goal-units" className={labelClass()}>
                  Units
                </label>
                {goalType === 'lift' ? (
                  <select
                    id="goal-units"
                    value={liftUnit}
                    onChange={(e) => setLiftUnit(e.target.value as 'kg' | 'lb')}
                    className={`${inputClass()} mt-1.5`}
                  >
                    <option value="kg">Kilograms (kg)</option>
                    <option value="lb">Pounds (lb)</option>
                  </select>
                ) : goalType === 'weight' || goalType === 'lean_mass' ? (
                  <select
                    id="goal-units"
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value as WeightUnit)}
                    className={`${inputClass()} mt-1.5`}
                  >
                    <option value="kg">Kilograms (kg)</option>
                    <option value="lb">Pounds (lb)</option>
                  </select>
                ) : goalType === 'body_fat' ? (
                  <div
                    id="goal-units"
                    className={`${inputClass()} mt-1.5 flex items-center text-zinc-400`}
                  >
                    Percent (%)
                  </div>
                ) : (
                  <input
                    id="goal-units"
                    placeholder="min, sec, reps, etc."
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                    className={`${inputClass()} mt-1.5`}
                  />
                )}
              </div>
            </div>

            {goalType !== 'lift' ? (
              <div>
                <label htmlFor="current-val" className={labelClass()}>
                  Current value (optional)
                </label>
                <input
                  id="current-val"
                  inputMode="decimal"
                  placeholder={`Override auto values; use same units as target (${goalType === 'weight' || goalType === 'lean_mass' ? weightUnit : goalType === 'body_fat' ? '%' : customUnit})`}
                  value={currentOptional}
                  onChange={(e) => setCurrentOptional(e.target.value)}
                  className={`${inputClass()} mt-1.5`}
                />
                <p className="mt-1.5 text-xs text-zinc-500">
                  {goalType === 'weight' ? (
                    <>
                      Leaving this empty uses your profile weight when available (converted to {weightUnit.toUpperCase()}
                      ).
                    </>
                  ) : goalType === 'lean_mass' ? (
                    <>
                      Leaving this empty estimates lean mass from your latest check-in weight and body-fat % (or profile
                      body fat).
                    </>
                  ) : (
                    <>Optional manual progress snapshot.</>
                  )}
                </p>
              </div>
            ) : null}

            <div>
              <label htmlFor="target-date" className={labelClass()}>
                Target date (optional)
              </label>
              <input
                id="target-date"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className={`${dateInputClass()} mt-1.5`}
              />
            </div>

            <div>
              <label htmlFor="goal-notes" className={labelClass()}>
                Why this goal / reminders (optional)
              </label>
              <textarea
                id="goal-notes"
                rows={3}
                placeholder="What will accountability look like? Any constraints (equipment, schedule, injury history)?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${inputClass()} mt-1.5 resize-y`}
              />
            </div>

            <button
              type="button"
              disabled={saving}
              onClick={() => void addGoal()}
              className={btnPrimary}
            >
              {saving ? 'Saving…' : 'Save goal'}
            </button>
          </div>

          <aside className="min-w-0 rounded-2xl border border-sky-500/25 bg-sky-500/10 p-4 lg:sticky lg:top-24 lg:self-start">
            <h3 className="text-sm font-semibold text-sky-200">Tips for this goal type</h3>
            <p className="mt-1 text-xs text-zinc-500">Practical habits that usually move the needle—adjust to your program and recovery.</p>
            <ul className="mt-4 space-y-3 text-xs leading-relaxed text-zinc-300">
              {formTips.map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-sky-400/80" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </GlassCard>
    </div>
  );
}
